import axios from 'axios';
import { BLOG_API_BASE_URL } from '../../../constants';
import { useEffect, useMemo, useState } from 'react';
import KickImage from 'Reuseables/KickImage';
import { IFetchResponseForPagination } from 'interface/http';
import { EArticleCategory, IArticleListData } from './Definitions/Article.d';

type TResponse = IFetchResponseForPagination<IArticleListData[]>;

export interface IUseFetchArticlesProps {
  category: EArticleCategory;
  tags?: string | string[];
  page?: number;
  perPage?: number;
  sortBy?: 'createdAt_desc' | 'createdAt_asc';
  stopFetch?: boolean;
}

export const useFetchArticles = (props: IUseFetchArticlesProps) => {
  const {
    category,
    tags,
    page = 1,
    perPage = 15,
    sortBy = 'createdAt_desc',
    stopFetch = false,
  } = props;
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    page,
    perPage,
  });
  const [response, setResponse] = useState<TResponse>();
  const [data, setData] = useState<IArticleListData[]>([]);
  const [infiniteData, setInfiniteData] = useState<IArticleListData[]>([]);

  const loadMore = () => {
    if (!isFetching && hasMoreData) {
      setPagination({
        page: pagination.page + 1,
        perPage: pagination.perPage,
      });
    }
  };

  const hasMoreData = useMemo(() => {
    if (response) {
      return infiniteData.length < response.data.total;
    }

    return false;
  }, [response, infiniteData])

  const isReady = useMemo(() => {
    return !isFetching && !!response;
  }, [isFetching, response]);

  const tagSlugQuery = useMemo(() => {
    let result = '';

    if (tags) {
      if (Array.isArray(tags) && tags.length > 0) {
        const lastIndex = tags.length - 1;

        tags.forEach((tagSlugs, index) => {
          result += tagSlugs;

          if (index !== lastIndex) {
            result += `,`;
          }
        });

        return result;
      }

      if (typeof tags === 'string') {
        result = tags;
      }
    }

    return result;
  }, [tags]);

  const fetchUrl = useMemo(() => {
    let url = `${BLOG_API_BASE_URL}/articles?page=${pagination.page}&per_page=${pagination.perPage}&sort_by=${sortBy}`;
    const virtualCategory = [EArticleCategory.Latest];

    if (category && !virtualCategory.includes(category)) {
      url += `&category=${category}`;
    }

    if (tagSlugQuery) {
      url += `&tags=${tagSlugQuery}`;
    }

    return url;
  }, [
    category,
    tagSlugQuery,
    pagination.page,
    pagination.perPage,
    sortBy,
    stopFetch,
  ]);

  const manualReset = () => {
    setIsError(false);
    setIsFetching(false);
    setInfiniteData([]);
    setData([]);
    setResponse(undefined);
    fetchArticles()
  }

  const fetchArticles = async () => {
    await axios
      .get<TResponse>(fetchUrl)
      .then((res) => {
        const responseData = res.data.data.data;

        setIsFetching(false);
        setIsError(false);
        setResponse(res.data);
        setData(responseData);
        setInfiniteData((prev) => {
          return [...prev, ...responseData];
        });
      })
      .catch((err) => {
        console.log('failed to fetch articles', err);

        setIsFetching(false);
        setIsError(true);
      });
  }

  useEffect(() => {
    setPagination({
      page,
      perPage,
    });
  }, [page, perPage]);

  useEffect(() => {
    if (stopFetch) {
      return;
    }

    setIsFetching(true);
    fetchArticles();

    return () => {
      setIsError(false);
      setIsFetching(false);
    };
  }, [fetchUrl, stopFetch]);

  useEffect(() => {
    return () => {
      setIsError(false);
      setIsFetching(false);
      setInfiniteData([]);
      setData([]);
      setResponse(undefined);
    };
  }, []);

  return {
    isFetching,
    isError,
    isReady,
    data,
    infiniteData,
    response,
    hasMoreData,
    loadMore,
    manualReset
  };
};
